import type { PageDefinition } from "@judo/model-api";
import { DialogSize } from "@judo/model-api";
import { type ReactNode, createContext, useCallback, useContext, useRef, useState } from "react";

/**
 * Page stack entry.
 */
export interface PageStackEntry {
	pageDefinition: PageDefinition;
	params?: Record<string, unknown>;
	scrollPosition?: number;
}

/**
 * Dialog close result types.
 */
export type DialogCloseResultType = "cancelled" | "created" | "updated" | "deleted" | "selected" | "submitted";

/**
 * Result returned when dialog closes.
 * Uses discriminated union for type-safe handling of different outcomes.
 */
export type DialogCloseResult =
	| { type: "cancelled" }
	| { type: "created"; isEager: boolean; data?: unknown }
	| { type: "updated"; data?: unknown }
	| { type: "deleted"; data?: unknown }
	| { type: "selected"; data?: unknown }
	| { type: "submitted"; data?: unknown };

/**
 * Dialog state.
 */
export interface DialogState {
	pageDefinition: PageDefinition;
	size: DialogSize;
	params?: Record<string, unknown>;
}

/**
 * Navigation state.
 */
export interface NavigationState {
	/** Current page stack (for back navigation) */
	pageStack: PageStackEntry[];
	/** Current page definition */
	currentPage: PageDefinition | null;
	/** Dialog stack — supports nested dialogs. Top of stack is the active dialog. */
	dialogStack: DialogState[];
}

/**
 * Callback invoked when dialog closes.
 */
export type DialogCloseCallback = (result?: DialogCloseResult) => void;

/**
 * Navigation context type.
 */
export interface NavigationContextType extends NavigationState {
	/** Top dialog (convenience — same as dialogStack[dialogStack.length - 1] or null) */
	dialog: DialogState | null;
	/** Navigate to page */
	navigateTo: (page: PageDefinition, params?: Record<string, unknown>) => void;
	/** Navigate back */
	goBack: () => void;
	/** Open page in dialog with optional onClose callback */
	openDialog: (page: PageDefinition, params?: Record<string, unknown>, onClose?: DialogCloseCallback) => void;
	/** Close current dialog with optional result */
	closeDialog: (result?: DialogCloseResult) => void;
	/** Replace current page (no stack) */
	replacePage: (page: PageDefinition, params?: Record<string, unknown>) => void;
	/** Clear navigation stack */
	clearStack: () => void;
	/** Sync navigation state with current route pathname (for browser back/forward) */
	syncLocation: (pathname: string) => void;
	/** Check if can go back */
	canGoBack: boolean;
}

const NavigationContext = createContext<NavigationContextType | null>(null);

/**
 * Hook to access navigation context.
 *
 * @returns NavigationContextType
 * @throws Error if used outside NavigationProvider
 */
export function useNavigation(): NavigationContextType {
	const context = useContext(NavigationContext);
	if (!context) {
		throw new Error("useNavigation must be used within NavigationProvider");
	}
	return context;
}

/**
 * Hook to access navigation context (optional).
 *
 * @returns NavigationContextType | null
 */
export function useNavigationOptional(): NavigationContextType | null {
	return useContext(NavigationContext);
}

/**
 * Props for NavigationProvider.
 */
export interface NavigationProviderProps {
	children: ReactNode;
	/** Initial page (optional) */
	initialPage?: PageDefinition;
	/** Optional navigate function for React Router integration */
	navigate?: (path: string, options?: { state?: unknown; replace?: boolean }) => void;
	/** Optional function to get route path for a page */
	getRouteForPage?: (page: PageDefinition) => string;
	/**
	 * Optional resolver for the fallback route used by `goBack()` when both the
	 * dialog stack and the page stack are empty. When provided, the returned route
	 * is used with `{ replace: true }` so the in-app Back action always lands on
	 * a known, app-scoped URL instead of a potentially stale browser history entry.
	 * Defaults to `"/"` when not provided.
	 */
	getFallbackRoute?: () => string;
}

/**
 * Provider for navigation context.
 */
export function NavigationProvider({
	children,
	initialPage,
	navigate,
	getRouteForPage,
	getFallbackRoute,
}: NavigationProviderProps) {
	const [state, setState] = useState<NavigationState>({
		pageStack: [],
		currentPage: initialPage ?? null,
		dialogStack: [],
	});

	const navigateTo = useCallback(
		(page: PageDefinition, params?: Record<string, unknown>) => {
			setState((prev) => {
				// Check if page should open in dialog
				if (page.openInDialog) {
					// Push to dialog stack (supports nested dialogs)
					dialogCloseCallbackStackRef.current.push(null);
					return {
						...prev,
						dialogStack: [
							...prev.dialogStack,
							{
								pageDefinition: page,
								size: page.dialogSize || DialogSize.MD,
								params,
							},
						],
					};
				}

				// Regular navigation - push to stack, clear all dialogs
				dialogCloseCallbackStackRef.current = [];
				const newStack = prev.currentPage ? [...prev.pageStack, { pageDefinition: prev.currentPage }] : prev.pageStack;

				return {
					...prev,
					pageStack: newStack,
					currentPage: page,
					dialogStack: [],
				};
			});

			// Integrate with React Router if navigate function is provided
			if (!page.openInDialog && navigate && getRouteForPage) {
				const route = getRouteForPage(page);

				// For pages that need transfer context (e.g., VIEW pages), append signed identifier to URL
				const transfer = params?.transfer as { __signedIdentifier?: string } | undefined;
				const finalRoute = transfer?.__signedIdentifier
					? `${route}/${encodeURIComponent(transfer.__signedIdentifier)}`
					: route;

				navigate(finalRoute, { state: params });
			}
		},
		[navigate, getRouteForPage]
	);

	const goBack = useCallback(() => {
		setState((prev) => {
			// If dialog stack is non-empty, pop the top dialog
			if (prev.dialogStack.length > 0) {
				// Invoke onClose callback for the top dialog
				const callback = dialogCloseCallbackStackRef.current.pop() ?? null;
				if (callback) {
					callback(undefined); // No result means cancelled
				}
				return { ...prev, dialogStack: prev.dialogStack.slice(0, -1) };
			}

			// Pop from stack
			if (prev.pageStack.length === 0) {
				// No internal stack — navigate to an app-scoped fallback route
				// (typically the active application's dashboard) rather than relying on
				// the browser history, which may contain URLs from a previous actor or
				// unrelated sites and can surface as 404 on the current <Routes> tree.
				if (navigate) {
					const fallbackRoute = getFallbackRoute?.() ?? "/";
					navigate(fallbackRoute, { replace: true });
				}
				return prev;
			}

			const newStack = [...prev.pageStack];
			const previousEntry = newStack.pop()!;

			// Navigate to previous page's route if router integration available
			if (navigate && getRouteForPage) {
				const route = getRouteForPage(previousEntry.pageDefinition);
				navigate(route, { state: previousEntry.params });
			}

			return {
				...prev,
				pageStack: newStack,
				currentPage: previousEntry.pageDefinition,
			};
		});
	}, [navigate, getRouteForPage, getFallbackRoute]);

	// Store dialog close callbacks in a stack (one per dialog in the stack)
	const dialogCloseCallbackStackRef = useRef<(DialogCloseCallback | null)[]>([]);

	const openDialog = useCallback(
		(page: PageDefinition, params?: Record<string, unknown>, onClose?: DialogCloseCallback) => {
			// Push the callback for this dialog
			dialogCloseCallbackStackRef.current.push(onClose ?? null);

			setState((prev) => ({
				...prev,
				dialogStack: [
					...prev.dialogStack,
					{
						pageDefinition: page,
						size: page.dialogSize || DialogSize.MD,
						params,
					},
				],
			}));
		},
		[]
	);

	const closeDialog = useCallback((result?: DialogCloseResult) => {
		// Pop and invoke the top dialog's callback
		const callback = dialogCloseCallbackStackRef.current.pop() ?? null;
		if (callback) {
			callback(result);
		}

		setState((prev) => ({ ...prev, dialogStack: prev.dialogStack.slice(0, -1) }));
	}, []);

	const replacePage = useCallback((page: PageDefinition, _params?: Record<string, unknown>) => {
		dialogCloseCallbackStackRef.current = [];
		setState((prev) => ({
			...prev,
			currentPage: page,
			dialogStack: [],
		}));
	}, []);

	const clearStack = useCallback(() => {
		setState((prev) => ({
			...prev,
			pageStack: [],
		}));
	}, []);

	const syncLocation = useCallback(
		(pathname: string) => {
			if (!getRouteForPage) return;

			setState((prev) => {
				// If current page matches the pathname, state is already in sync
				if (prev.currentPage) {
					const currentRoute = getRouteForPage(prev.currentPage);
					// Match both exact route and route with trailing params (e.g., /view/signedId)
					if (pathname === currentRoute || pathname.startsWith(`${currentRoute}/`)) {
						return prev;
					}
				}

				// Search pageStack from top to find matching entry
				for (let i = prev.pageStack.length - 1; i >= 0; i--) {
					const entry = prev.pageStack[i];
					const entryRoute = getRouteForPage(entry.pageDefinition);
					if (pathname === entryRoute || pathname.startsWith(`${entryRoute}/`)) {
						// Truncate stack to this point, set this as current page
						return {
							...prev,
							pageStack: prev.pageStack.slice(0, i),
							currentPage: entry.pageDefinition,
							dialogStack: [],
						};
					}
				}

				// No match found (e.g., navigating to "/" or unknown route) — clear stack
				return {
					...prev,
					pageStack: [],
					currentPage: null,
					dialogStack: [],
				};
			});
		},
		[getRouteForPage]
	);

	const dialog = state.dialogStack.length > 0 ? state.dialogStack[state.dialogStack.length - 1] : null;

	const value: NavigationContextType = {
		...state,
		dialog,
		navigateTo,
		goBack,
		openDialog,
		closeDialog,
		replacePage,
		clearStack,
		syncLocation,
		canGoBack: state.pageStack.length > 0 || state.dialogStack.length > 0,
	};

	return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}
